<template>
  <main class="min-h-screen bg-gray-200">
    <header class="">
      <div class="max-w-2xl">
        <div
          class="border-b border-gray-200 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
        >
          <div class="flex-1 min-w-0">
            <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
              {{ $t('patients_title') }}
            </h1>
          </div>
          <div class="mt-4 sm:mt-0 space-x-2 hidden md:block">
            <sqr-button
              :label="$t('patients_index')"
              color="transparent"
              second="green"
              @click="index()"
              :loading="indexing"
            />
            <span class="">
              <sqr-button
                :label="$t('patients_export')"
                color="transparent"
                second="green"
                @click="exportPatients()"
                :loading="exporting"
              />
            </span>
            <span class="shadow-sm rounded-md">
              <router-link :to="{ name: 'patient-add' }">
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:shadow-outline-purple focus:border-green-700 active:bg-green-700 transition duration-150 ease-in-out"
                >
                  {{ $t('patients_add') }}
                </button>
              </router-link>
            </span>
          </div>
        </div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner
        :error="loadError"
        @retry="subscribe({ reload: true })"
      />
    </header>

    <section class="lg:px-4 max-w-2xl py-4">
      <div class="bg-white overflow-hidden lg:rounded shadow">
        <ul>
          <li
            v-for="patient in patients"
            :key="patient.id"
            class="border-b border-gray-200"
          >
            <router-link
              :to="{ name: 'patient', params: { aid, id: patient.id } }"
              class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
            >
              <div class="px-4 py-4 sm:px-6">
                <div class="text-sm leading-5 text-blue-600 truncate">
                  <span class="font-bold"> {{ patient.familyName }}, </span>
                  <span class="font-normal">
                    {{ patient.givenName }}
                  </span>
                </div>
                <div class="mt-2 sm:flex sm:justify-between">
                  <div class="sm:flex">
                    <div
                      class="mr-6 flex items-center text-sm leading-5 text-gray-600 font-light"
                    >
                      <CalendarIcon
                        class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      />
                      <date
                        :value="patient.birthDate"
                        :options="{
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }"
                      />
                    </div>
                    <div
                      class="mt-2 flex items-center text-sm leading-5 text-gray-600 font-light sm:mt-0"
                    >
                      <LocationMarkerIcon
                        class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      />
                      <span>{{ patient.addressLocality }}</span>
                    </div>
                  </div>
                  <div
                    class="mt-2 flex items-center text-sm leading-5 text-gray-600 font-light sm:mt-0"
                    v-if="patient.telephone"
                  >
                    <PhoneIcon
                      class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    />
                    <span>
                      <a
                        v-if="patient.telephone"
                        :href="tel(patient.telephone)"
                        >{{ patient.telephone }}</a
                      >
                    </span>
                  </div>
                </div>
              </div>
            </router-link>
          </li>
        </ul>
      </div>
    </section>

    <section class="py-12" v-if="patients.length === 0">
      <div class="md:p-4 max-w-2xl">
        <div class="grid grid-cols-2 gap-x-6 items-center">
          <div class="col-span-1"><People /></div>
          <div class="col-span-1 text-lg">
            <i18n path="patients_empty" for="tos">
              <router-link
                :to="{ name: 'patient-add', params: { aid } }"
                class="text-blue-600 hover:text-blue-500 focus:text-blue-500 underline"
                >{{ $t('patients_empty_link') }}</router-link
              >
            </i18n>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import {
  CalendarIcon,
  LocationMarkerIcon,
  PhoneIcon
} from '@vue-hero-icons/outline';

import zipcelx from 'zipcelx';

import { mapState, mapActions } from 'vuex';
import { pick, fromPairs, sortWith, ascend, prop } from 'ramda';

import SqrProgress from '@/components/SqrProgress';
import SqrErrorBanner from '@/components/SqrErrorBanner';
import SqrButton from '@/components/SqrButton';
import Date from '@/components/Date';
import People from '@/components/People';

import date from '@/filters/date';

const sortByName = sortWith([
  ascend(prop('familyName')),
  ascend(prop('givenName'))
]);

export default {
  name: 'Patients',
  components: {
    SqrButton,
    SqrProgress,
    SqrErrorBanner,
    Date,
    People,
    CalendarIcon,
    LocationMarkerIcon,
    PhoneIcon
  },
  filters: { date },
  props: {
    aid: String
  },
  computed: {
    ...mapState('patientsIndex', ['doc', 'loading', 'loadError']),
    patients() {
      if (!this.doc) return [];
      const patients = Object.entries(this.doc?.patients ?? {}).map(
        ([id, patient]) => ({
          ...patient,
          id
        })
      );
      return sortByName(patients);
    }
  },
  data() {
    return {
      indexing: false,
      exporting: false,
      exportError: null
    };
  },
  mounted() {
    this.$a().logEvent('patients');
    const path = `accounts/${this.aid}/indexes/patients`;
    this.sub({ path });
  },
  methods: {
    ...mapActions('patientsIndex', ['sub']),
    tel(number) {
      return 'tel:' + number.replace(/\s/g, '');
    },
    async removeConfirm(patient) {
      try {
        if (confirm(this.$t('patients_remove_confirm', patient))) {
          await this.remove({ id: patient.id });
        }
      } catch (error) {
        console.log(error);
      }
    },
    async index() {
      try {
        this.indexing = true;
        const aid = this.aid;
        const fields = [
          'givenName',
          'familyName',
          'birthDate',
          'addressLocality'
        ];
        const snaps = await this.$db()
          .collection(`accounts/${aid}/patients`)
          .get();
        const entries = snaps.docs.map(snap => [
          snap.id,
          pick(fields, snap.data())
        ]);
        const patients = fromPairs(entries);
        const index = { patients };
        await this.$db()
          .doc(`accounts/${aid}/indexes/patients`)
          .set(index);
      } finally {
        this.indexing = false;
      }
    },
    async exportPatients() {
      try {
        this.exporting = true;
        this.exportError = null;

        const res = await this.$db()
          .collection('accounts')
          .doc(this.aid)
          .collection('patients')
          .get();

        const raw = res.docs.map(doc => doc.data());
        const patients = sortByName(raw);
        const rows = patients.map(patient => [
          { value: patient.familyName ?? '', type: 'string' },
          { value: patient.givenName ?? '', type: 'string' },
          { value: date(patient.birthDate) || '', type: 'string' },
          { value: patient.gender ?? '', type: 'string' },
          { value: patient.email, type: 'string' },
          { value: patient.telephone, type: 'string' },
          { value: patient.addressStreet, type: 'string' },
          { value: patient.postalCode, type: 'string' },
          { value: patient.addressLocality, type: 'string' },
          { value: patient.canton, type: 'string' },
          { value: patient.law, type: 'string' },
          { value: patient.refundType, type: 'string' },
        ]);

        const config = {
          filename: 'Simple590-Patients-export',
          sheet: {
            data: [
              [
                { value: 'Nom', type: 'string' },
                { value: 'Prénom', type: 'string' },
                { value: 'Naissance', type: 'string' },
                { value: 'Genre', type: 'string' },
                { value: 'e-mail', type: 'string' },
                { value: 'Telephone', type: 'string' },
                { value: 'Rue', type: 'string' },
                { value: 'NPA', type: 'string' },
                { value: 'Ville', type: 'string' },
                { value: 'Canton', type: 'string' },
                { value: 'Loi', type: 'string' },
                { value: 'Remboursement', type: 'string' }
              ],
              ...rows,
              [],
              [
                { value: 'Exporté par Simple590 https://www.simple590.ch/', type: 'string' },
              ]
            ]
          }
        };

        zipcelx(config);
      } catch (error) {
        console.log(error);
        this.exportError = error;
        return Promise.reject(error);
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>
